import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, Calendar, Clock, Home, CheckCircle, XCircle, AlertCircle, MapPin } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { formatDateLocal } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentDetail = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { token } = useAuth();
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentDetails();
  }, [agentId]);

  const fetchAgentDetails = async () => {
    try {
      const response = await axios.get(`${API}/broker/agents/${agentId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentData(response.data);
    } catch (error) {
      toast.error('Failed to fetch agent details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-stone-600">Loading agent details...</p>
        </div>
      </Layout>
    );
  }

  if (!agentData) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-stone-600">Agent not found</p>
          <Button onClick={() => navigate('/broker')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  const { agent, assignments, personal_events } = agentData;

  // Sort assignments by date
  const sortedAssignments = [...assignments].sort((a, b) => new Date(a.date) - new Date(b.date));
  const sortedEvents = [...personal_events].sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/broker')}
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Agent Profile
          </h1>
        </div>

        {/* Agent Profile Card */}
        <Card className="p-8 bg-white rounded-2xl card-shadow mb-8">
          <div className="flex items-start gap-6">
            <div className="p-6 rounded-full bg-[#1A4D2E]/10">
              <User className="text-[#1A4D2E]" size={48} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-3" style={{fontFamily: '"Playfair Display", serif'}}>
                {agent.name}
              </h2>
              <div className="space-y-2 mb-4">
                <p className="text-stone-600 flex items-center gap-2">
                  <Mail size={18} />
                  {agent.email}
                </p>
                {agent.phone && (
                  <p className="text-stone-600 flex items-center gap-2">
                    <Phone size={18} />
                    {formatPhoneDisplay(agent.phone)}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                  agent.status === 'approved' || agent.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : agent.status === 'rejected' 
                    ? 'bg-red-100 text-red-800' 
                    : agent.status === 'inactive'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {agent.status === 'approved' || agent.status === 'active' ? '✓ Active' : 
                   agent.status === 'rejected' ? '✗ Rejected' : 
                   agent.status === 'inactive' ? '⊗ Inactive' : 
                   '⏳ Pending'}
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  {assignments.length} Total Assignments
                </span>
                {agent.show_open_houses_to_broker && (
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                    {personal_events.length} Personal Open Houses
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Assignments Section */}
        <Card className="p-8 bg-white rounded-2xl card-shadow mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
            Open House Assignments ({assignments.length})
          </h2>
          
          {assignments.length === 0 ? (
            <div className="text-center py-12 bg-stone-50 rounded-lg">
              <Calendar className="mx-auto mb-4 text-stone-300" size={64} />
              <p className="text-stone-600">No assignments yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedAssignments.map((assignment) => (
                <Card key={assignment.id} className={`p-6 ${
                  assignment.status === 'accepted' ? 'border-2 border-green-200 bg-green-50' :
                  assignment.status === 'rejected' ? 'border border-red-200 bg-red-50 opacity-60' :
                  'border-2 border-orange-200 bg-orange-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          assignment.status === 'accepted' ? 'bg-green-200 text-green-800' :
                          assignment.status === 'rejected' ? 'bg-red-200 text-red-800' :
                          'bg-orange-200 text-orange-800'
                        }`}>
                          {assignment.status === 'accepted' && <CheckCircle className="inline mr-1" size={14} />}
                          {assignment.status === 'rejected' && <XCircle className="inline mr-1" size={14} />}
                          {assignment.status === 'pending' && <AlertCircle className="inline mr-1" size={14} />}
                          {assignment.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="font-semibold text-[#1A4D2E] mb-2 flex items-center gap-2">
                            <MapPin size={18} />
                            {assignment.property_address}
                          </p>
                          <p className="text-stone-600 text-sm flex items-center gap-2">
                            <Calendar size={14} />
                            {formatDateLocal(assignment.date)}
                          </p>
                          <p className="text-stone-600 text-sm flex items-center gap-2">
                            <Clock size={14} />
                            {assignment.time}{assignment.end_time ? ` - ${assignment.end_time}` : ''}
                          </p>
                        </div>
                        <div>
                          {assignment.description && (
                            <p className="text-stone-600 text-sm mb-2">
                              <strong>Broker Note:</strong> {assignment.description}
                            </p>
                          )}
                          {assignment.agent_note && (
                            <p className="text-green-700 text-sm italic">
                              <strong>Agent Response:</strong> "{assignment.agent_note}"
                            </p>
                          )}
                          {assignment.event_id && (
                            <p className="text-blue-600 text-sm font-semibold mt-2">
                              ✓ Open house event created
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Personal Open Houses Section - only visible if agent has enabled sharing */}
        {agent.show_open_houses_to_broker && (
        <Card className="p-8 bg-white rounded-2xl card-shadow">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
            Personal Open Houses ({personal_events.length})
          </h2>
          
          {personal_events.length === 0 ? (
            <div className="text-center py-12 bg-stone-50 rounded-lg">
              <Home className="mx-auto mb-4 text-stone-300" size={64} />
              <p className="text-stone-600">No personal open houses created yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedEvents.map((event) => (
                <Card key={event.id} className="p-6 border-2 border-blue-200 bg-blue-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-200 text-blue-800">
                          <Home className="inline mr-1" size={14} />
                          PERSONAL EVENT
                        </span>
                      </div>
                      <p className="font-semibold text-[#1A4D2E] mb-2 flex items-center gap-2">
                        <MapPin size={18} />
                        {event.property_address}
                      </p>
                      <p className="text-stone-600 text-sm flex items-center gap-2">
                        <Calendar size={14} />
                        {(() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }); })()}
                      </p>
                      <p className="text-stone-600 text-sm flex items-center gap-2">
                        <Clock size={14} />
                        {event.time}{event.end_time ? ` - ${event.end_time}` : ''}
                      </p>
                      {event.description && (
                        <p className="text-stone-600 text-sm mt-2">
                          <strong>Description:</strong> {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
        )}
      </div>
    </Layout>
  );
};

export default AgentDetail;
